import { useEffect, useRef, useState } from 'react'
import { api, ApiError, can, type NumberChoice } from '../lib/api'
import { ClipButton, PictureRow, usePictures } from './PictureTray'
import { PhoneInput } from './PhoneInput'

/** NEW CONVERSATION (6 Oct 2026): text somebody first.
 *
 *  The customer: "a button start new conversation. And I should be able to
 *  text any random number or anyone from my phone book or anyone from the
 *  members." On a member with several numbers: "By default, it should be his
 *  main number, but I should have the option to start a conversation with
 *  any of his numbers." A typed number that belongs to somebody goes into
 *  their conversation (his Q2 A) -- the server decides that.
 *
 *  A MEMBER'S OR A CONTACT'S NUMBER STAYS BEHIND THE NAME: the choice of
 *  number is a label and the last four digits. Only a typed number is shown.
 *
 *  Every class carries `nc-`: theme.css owns the short names. */

type Who = 'member' | 'contact' | 'number'
type Picked = { id: string; label: string }

export function NewConversation({ me, onStarted, onCancel }: {
  me: { display_name: string }
  onStarted: (key: string) => void
  onCancel: () => void
}) {
  const [who, setWho] = useState<Who>('member')
  const [term, setTerm] = useState('')
  const [found, setFound] = useState<(Picked & { sub?: string })[]>([])
  const [picked, setPicked] = useState<Picked | null>(null)
  const [nums, setNums] = useState<NumberChoice[] | null>(null)
  const [phoneId, setPhoneId] = useState<string | null>(null)
  const [typed, setTyped] = useState('')
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const pics = usePictures()
  const [error, setError] = useState<string | null>(null)
  const ask = useRef(0)

  // A different kind of person is a fresh choice.
  useEffect(() => { setTerm(''); setFound([]); setPicked(null); setNums(null); setError(null) }, [who])

  // The roster's own search (item 18) and the phone book's.
  useEffect(() => {
    const mine = ++ask.current
    if (who === 'number' || picked || term.trim().length < 1) { setFound([]); return }
    const t = setTimeout(() => {
      const q = term.trim()
      const go = who === 'member'
        ? api.memberPick(q).then((r) => r.map((x) => ({
            id: x.id, label: x.label, sub: x.on_duty ? 'on duty' : undefined })))
        : api.contacts(q, 8).then((r) => r.map((x) => ({
            id: x.id, label: x.display_name ?? 'No name', sub: 'phone book' })))
      go.then((r) => { if (ask.current === mine) setFound(r) })
        .catch((e) => {
          if (ask.current !== mine) return
          setFound([])
          setError(e instanceof ApiError ? e.message : 'Could not search.')
        })
    }, 200)
    return () => clearTimeout(t)
  }, [term, who, picked])

  // His numbers, main first and chosen unless he picks another.
  useEffect(() => {
    if (!picked || who === 'number') { setNums(null); setPhoneId(null); return }
    let alive = true
    api.messagingNumbers(who === 'member' ? { member_id: picked.id } : { contact_id: picked.id })
      .then((r) => {
        if (!alive) return
        setNums(r)
        setPhoneId(r.find((x) => x.default)?.id ?? null)
      })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : 'Could not read the numbers.') })
    return () => { alive = false }
  }, [picked, who])

  const signed = text.trim() ? `${text.trim()}\n— ${me.display_name}` : ''
  const chars = [...signed].length
  const recipientReady = who === 'number' ? typed.replace(/\D/g, '').length >= 7
    : !!picked && !!phoneId
  const ready = recipientReady && (text.trim().length > 0 || pics.ids.length > 0)
    && !pics.busy && !sending

  async function send() {
    setSending(true)
    setError(null)
    try {
      const out = await api.messagingStart(
        who === 'number' ? { number: typed.trim(), body: text.trim(), attachment_ids: pics.ids }
        : who === 'member' ? { member_id: picked!.id, phone_id: phoneId ?? undefined, body: text.trim(), attachment_ids: pics.ids }
        : { contact_id: picked!.id, phone_id: phoneId ?? undefined, body: text.trim(), attachment_ids: pics.ids })
      onStarted(out.key)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send it.')
    } finally {
      setSending(false)
    }
  }

  const numberWord = (n: NumberChoice) =>
    `${n.label || 'Number'} · ends ${n.last4}${n.main ? ' (main)' : ''}`

  return (
    <div className="nc">
      <div className="msghead">
        <button type="button" className="btn sm back" onClick={onCancel}>←</button>
        <b>New conversation</b>
        <span className="spacer" />
        <button type="button" className="btn sm" onClick={onCancel}>Cancel</button>
      </div>
      <div className="nc-body">
        <section>
          <div className="nc-lab">1. Who</div>
          <div className="nc-seg" role="group" aria-label="Who">
            {/* The phone book is searched with its own tick (`search.callers`);
                a role without it is not offered the door. */}
            {([['member', 'A member'], ['contact', 'Phone book'], ['number', 'Any number']] as const)
              .filter(([k]) => k !== 'contact' || can('search.callers')).map(([k, l]) => (
              <button type="button" key={k} aria-pressed={who === k}
                      className={who === k ? 'nc-on' : ''} onClick={() => setWho(k)}>{l}</button>
            ))}
          </div>
        </section>

        {who !== 'number' && !picked && (
          <section className="nc-search">
            <input className="nc-input" autoFocus value={term} autoComplete="off"
                   onChange={(e) => setTerm(e.target.value)}
                   placeholder={who === 'member' ? 'Type a name, unit number or phone number…'
                                                 : 'Type a name or phone number…'} />
            {found.length > 0 && (
              <div className="nc-found" role="listbox">
                {found.slice(0, 8).map((p) => (
                  <button type="button" key={p.id} role="option" aria-selected={false}
                          onClick={() => setPicked({ id: p.id, label: p.label })}>
                    {p.label}{p.sub && <small>{p.sub}</small>}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {who !== 'number' && picked && (
          <section className="nc-to">
            <div>To <b>{picked.label}</b>
              <button type="button" className="nc-link" onClick={() => { setPicked(null); setTerm('') }}>change</button>
            </div>
            {nums && nums.length === 0 && (
              <div className="nc-warn">No number that can be texted is on {who === 'member' ? 'his record' : 'this entry'}.</div>
            )}
            {nums && nums.length > 1 && (
              <div className="nc-nums" role="radiogroup" aria-label="Which number">
                {nums.map((n) => (
                  <label key={n.id} className={phoneId === n.id ? 'nc-on' : ''}>
                    <input type="radio" name="nc-num" checked={phoneId === n.id}
                           onChange={() => setPhoneId(n.id)} />
                    {numberWord(n)}
                  </label>
                ))}
              </div>
            )}
            {nums && nums.length === 1 && <div className="nc-hint">On {numberWord(nums[0])}.</div>}
          </section>
        )}

        {who === 'number' && (
          <section>
            <PhoneInput className="nc-input nc-mono" autoFocus value={typed} inputMode="tel"
                   onChange={(e) => setTyped(e.target.value)} placeholder="Type the number" />
            <div className="nc-hint">Any number. If it belongs to a member or someone in the phone book,
              the text goes into their conversation.</div>
          </section>
        )}

        <section>
          <div className="nc-labrow">
            <label className="nc-lab" htmlFor="nc-text">2. Message</label>
            <ClipButton onFiles={pics.add} disabled={sending} />
          </div>
          <PictureRow pictures={pics.pictures} onRemove={pics.remove} />
          <textarea id="nc-text" className="nc-input nc-area" value={text} maxLength={1500}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Write the text. It is signed with your name." />
          <div className="nc-hint nc-right">{chars} characters with your signature</div>
        </section>

        {error && <div className="msgnote warn">{error}</div>}

        <div className="nc-actions">
          <span className="nc-hint">Signed “— {me.display_name}”. Their reply comes back into this conversation.</span>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn nc-go" disabled={!ready} onClick={send}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}
